import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { parseNumber } from '@df/util/fn/parse-number';

@Component({
    selector: 'single-file-upload',
    templateUrl: './single-file-upload.html',
    styleUrls: ['./single-file-upload.scss'],
    standalone: true,
    imports: [CommonModule, TranslationModule]
})
export class SingleFileUploadComponent {
    @Input()
    file: File | null = null;

    @Input()
    label: string = '';

    @Input()
    name: string = '';

    @Input()
    required: boolean = false;

    @Input()
    accept?: string;

    @Input()
    maxSize?: string | number;

    @Input()
    dragDropText?: string;

    @Input()
    openBrowserText?: string;

    @Output()
    fileChange = new EventEmitter<File | null>();

    protected isDragOver = false;
    protected errors: string[] = [];

    protected onFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            this.setFile(input.files[0]);
            // Clear the input so the same file can be selected again
            input.value = '';
        }
    }

    protected onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    protected onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    protected onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
            this.setFile(event.dataTransfer.files[0]);
        }
    }

    protected removeFile(): void {
        this.file = null;
        this.errors = [];
        this.fileChange.emit(null);
    }

    protected formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    private setFile(newFile: File): void {
        this.errors = [];
        const validationResult = this.validateFile(newFile);

        if (validationResult.isValid) {
            this.file = newFile;
            this.fileChange.emit(this.file);
        } else {
            this.errors = validationResult.errors;
            // Don't set the file if validation fails
        }
    }

    private validateFile(file: File): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        // Validate file size
        if (this.maxSize) {
            const maxSizeBytes = typeof this.maxSize === 'string' ? parseNumber(this.maxSize) : this.maxSize;
            if (file.size > maxSizeBytes) {
                const maxSizeFormatted = typeof this.maxSize === 'string' ? this.maxSize : this.formatFileSize(this.maxSize);
                errors.push(`File "${file.name}" is too large. Maximum size is ${maxSizeFormatted}.`);
            }
        }

        // Validate file type
        if (this.accept) {
            const fileType = file.type?.toLowerCase();
            const fileName = file.name?.toLowerCase();

            if (!this.isFileTypeAllowed(fileType, fileName)) {
                errors.push(`File "${file.name}" has an invalid format. Allowed formats: ${this.accept}.`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    private isFileTypeAllowed(fileType: string, fileName: string): boolean {
        if (!this.accept) return true;

        const acceptedTypes = this.accept
            .toLowerCase()
            .split(',')
            .map(type => type.trim());

        for (const acceptedType of acceptedTypes) {
            // Check MIME type (e.g., "image/jpeg")
            if (acceptedType.includes('/')) {
                if (acceptedType.includes('*')) {
                    // Handle wildcards like "image/*"
                    const baseType = acceptedType.split('/')[0];
                    if (fileType.startsWith(baseType + '/')) {
                        return true;
                    }
                } else if (fileType === acceptedType) {
                    return true;
                }
            }
            // Check file extension (e.g., ".jpg", "jpg")
            else {
                const extension = acceptedType.startsWith('.') ? acceptedType : '.' + acceptedType;
                if (fileName.endsWith(extension)) {
                    return true;
                }
            }
        }

        return false;
    }
}
